import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { ArrowRight, HeartPulse, Stethoscope, Activity, Ear, ShieldPlus, Ribbon, Scissors, Smile, Heart, ChevronRight } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { SERVICES } from "../lib/hospitalData";

const ICONS = {
  "heart-pulse": HeartPulse, stethoscope: Stethoscope, activity: Activity,
  ear: Ear, "shield-plus": ShieldPlus, ribbon: Ribbon, scissors: Scissors,
  smile: Smile, heart: Heart,
};

export default function Services() {
  const { hash } = useLocation();
  const [selectedId, setSelectedId] = useState(SERVICES[0].id);
  const [animKey, setAnimKey] = useState(0);

  const selected = SERVICES.find((s) => s.id === selectedId) || SERVICES[0];

  useEffect(() => {
    if (hash) {
      const id = hash.slice(1);
      if (SERVICES.some((s) => s.id === id)) setSelectedId(id);
      const el = document.getElementById(id);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
    }
  }, [hash]);

  // re-trigger reveal animation each time selection changes
  useEffect(() => { setAnimKey((k) => k + 1); }, [selectedId]);

  const SelectedIcon = ICONS[selected.icon] || HeartPulse;

  return (
    <main data-testid="page-services">
      <section className="bg-brand-cream relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-40" />
        <div className="container-x py-14 md:py-24 relative">
          <div className="text-xs tracking-[0.25em] uppercase text-brand-crimson font-semibold">Scope of Services</div>
          <h1 className="font-display text-[34px] sm:text-5xl lg:text-6xl font-bold text-brand-navy-deep mt-4 max-w-4xl leading-[1.05]">
            Super-Specialty Care, <span className="italic text-brand-crimson">All Under One Roof.</span>
          </h1>
          <p className="mt-5 md:mt-6 text-base md:text-lg text-slate-700 max-w-3xl leading-relaxed">
            Ten integrated specialties, anchored by full-time interventional cardiology and cardiothoracic surgery — designed to keep advanced treatment, emergencies and follow-up close to your home.
          </p>
        </div>
      </section>

      {/* Service Explorer with Dropdown */}
      <section className="py-12 md:py-20" data-testid="service-explorer">
        <div className="container-x">
          <div className="bg-white border border-border rounded-3xl overflow-hidden shadow-[0_24px_60px_-30px_rgba(19,60,121,0.25)]">
            <div className="grid lg:grid-cols-12">
              {/* Left: dropdown selector */}
              <div className="lg:col-span-5 p-6 md:p-10 bg-brand-cream/60 border-b lg:border-b-0 lg:border-r border-border">
                <div className="text-[11px] tracking-[0.25em] uppercase text-brand-crimson font-bold">Explore a Specialty</div>
                <h2 className="font-display text-2xl md:text-4xl text-brand-navy-deep mt-3 leading-tight">
                  Pick a service to learn more
                </h2>
                <p className="text-sm text-slate-600 mt-3 leading-relaxed">
                  Choose any specialty from the dropdown below — the full description, key procedures, and a way to book a consultation will appear instantly.
                </p>

                <div className="mt-6 md:mt-7">
                  <label htmlFor="service-select-trigger" className="text-xs font-semibold text-slate-700 mb-2 block uppercase tracking-wider">
                    Select Service
                  </label>
                  <Select value={selectedId} onValueChange={setSelectedId}>
                    <SelectTrigger
                      id="service-select-trigger"
                      data-testid="service-dropdown-trigger"
                      className="h-14 w-full bg-white border-brand-navy/20 px-4 text-base font-semibold text-brand-navy-deep rounded-xl focus:ring-2 focus:ring-brand-navy/20"
                    >
                      <SelectValue placeholder="Choose a specialty…" />
                    </SelectTrigger>
                    <SelectContent
                      data-testid="service-dropdown-content"
                      className="rounded-xl border-border bg-white max-h-[360px]"
                    >
                      {SERVICES.map((s) => {
                        const I = ICONS[s.icon] || HeartPulse;
                        return (
                          <SelectItem
                            key={s.id}
                            value={s.id}
                            data-testid={`service-option-${s.id}`}
                            className="cursor-pointer py-3 pr-8 pl-3 text-sm font-medium text-slate-800 focus:bg-brand-cream focus:text-brand-navy-deep data-[state=checked]:text-brand-crimson rounded-lg my-0.5"
                          >
                            <span className="inline-flex items-center gap-2.5">
                              <I className="w-4 h-4 text-brand-navy" />
                              {s.name}
                            </span>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>

                {/* Quick chips */}
                <div className="mt-6">
                  <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Quick jump</div>
                  <div className="flex flex-wrap gap-2">
                    {SERVICES.slice(0, 6).map((s) => (
                      <button
                        key={s.id}
                        data-testid={`service-chip-${s.id}`}
                        onClick={() => setSelectedId(s.id)}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-300 ${
                          selectedId === s.id
                            ? "bg-brand-crimson text-white border border-brand-crimson shadow-sm"
                            : "bg-white text-slate-700 border border-border hover:border-brand-navy hover:text-brand-navy-deep"
                        }`}
                      >
                        {s.name}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right: animated description panel */}
              <div className="lg:col-span-7 relative overflow-hidden">
                <div
                  key={animKey}
                  data-testid="service-detail-panel"
                  className="p-6 md:p-12 reveal"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-brand-navy-deep text-white flex items-center justify-center shadow-md shrink-0">
                      <SelectedIcon className="w-6 h-6 md:w-7 md:h-7" />
                    </div>
                    <div>
                      <div className="text-[10.5px] tracking-[0.25em] uppercase text-brand-crimson font-bold">Selected Specialty</div>
                      <h3 data-testid="selected-service-name" className="font-display text-xl md:text-3xl text-brand-navy-deep mt-1">
                        {selected.name}
                      </h3>
                    </div>
                  </div>

                  <p data-testid="selected-service-desc" className="text-slate-700 mt-5 md:mt-6 leading-relaxed text-[14.5px] md:text-[15px]">
                    {selected.desc}
                  </p>

                  <div className="mt-6 md:mt-7 grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {[
                      { label: "Care Setting", value: "OPD · IPD · ICU" },
                      { label: "Availability", value: "24×7 Emergency" },
                      { label: "Coverage", value: "Govt. Schemes" },
                    ].map((f, i) => (
                      <div key={f.label} className={`bg-brand-cream rounded-xl p-3.5 border border-border reveal reveal-${i+1}`}>
                        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{f.label}</div>
                        <div className="text-sm font-semibold text-brand-navy-deep mt-1">{f.value}</div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-7 md:mt-8 flex flex-wrap gap-3">
                    <Link to="/contact" data-testid="cta-book-selected" className="btn-primary">
                      Book a consultation <ArrowRight className="w-4 h-4" />
                    </Link>
                    <Link to="/doctors" className="btn-outline">
                      Meet our doctors <ChevronRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>

                {/* Decorative pulse */}
                <div className="pointer-events-none absolute -bottom-12 -right-12 w-56 h-56 rounded-full bg-brand-crimson/5 blur-3xl" />
                <div className="pointer-events-none absolute -top-12 -left-12 w-44 h-44 rounded-full bg-brand-navy/5 blur-3xl" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Full grid */}
      <section className="pb-16 md:pb-20" data-testid="services-grid">
        <div className="container-x">
          <div className="flex items-end justify-between mb-6 md:mb-8">
            <div>
              <div className="text-[11px] tracking-[0.25em] uppercase text-brand-crimson font-bold">All Specialties</div>
              <h2 className="font-display text-2xl md:text-4xl font-bold text-brand-navy-deep mt-2">Browse the full list</h2>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-5 md:gap-6">
            {SERVICES.map((s, i) => {
              const Icon = ICONS[s.icon] || HeartPulse;
              const isActive = selectedId === s.id;
              return (
                <article
                  id={s.id}
                  key={s.id}
                  className={`card-lift bg-white border rounded-2xl p-5 md:p-8 reveal reveal-${(i%5)+1} transition-colors scroll-mt-32 ${isActive ? "border-brand-crimson" : "border-border"}`}
                >
                  <div className="flex items-start gap-4 md:gap-5">
                    <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-brand-navy-deep text-white flex items-center justify-center shrink-0">
                      <Icon className="w-6 h-6 md:w-7 md:h-7" />
                    </div>
                    <div className="flex-1">
                      <h2 className="font-display text-xl md:text-2xl text-brand-navy-deep">{s.name}</h2>
                      <p className="text-slate-600 mt-2 md:mt-3 leading-relaxed text-sm md:text-base">{s.desc}</p>
                      <div className="mt-4 md:mt-5 flex flex-wrap gap-3 md:gap-4">
                        <button
                          onClick={() => {
                            setSelectedId(s.id);
                            document.querySelector('[data-testid="service-explorer"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
                          }}
                          className="inline-flex items-center gap-1 text-sm font-semibold text-brand-navy-deep hover:text-brand-crimson"
                        >
                          View in selector <ArrowRight className="w-4 h-4" />
                        </button>
                        <Link to="/contact" className="inline-flex items-center gap-1 text-sm font-semibold text-brand-crimson hover:underline">
                          Book consultation <ArrowRight className="w-4 h-4" />
                        </Link>
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="section-navy">
        <div className="container-x py-12 md:py-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-5 md:gap-6">
          <div>
            <h2 className="font-display text-2xl md:text-4xl font-bold leading-tight">Not sure which specialty fits you?</h2>
            <p className="text-white/80 mt-2 text-sm md:text-base">Send us a message — our care team will guide you to the right doctor.</p>
          </div>
          <Link to="/contact" className="btn-primary">Talk to Our Team <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </section>
    </main>
  );
}
